import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  ArrowRight,
  CheckCircle2,
  Circle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Project Management — Goals/Strategies/Projects/Tasks/Milestones data and
 * UI, now split into two reusable section components (`GoalsStrategiesSection`
 * and `ProjectsTasksSection`) so Project Manager can open each in its own
 * dialog while sharing the same lifted state (see ProjectManager.jsx).
 *
 * Everything here is local component state + hand-written sample data.
 * Nothing is sent to a backend, nothing is persisted, and nothing here
 * creates a real ContentProject. This is Level 1 (visual) + Level 2
 * (simulated interaction) only, as scoped.
 */

export const GOAL_CATEGORIES = ['Revenue', 'Audience', 'Engagement', 'Content Output', 'Monetization'];
export const PRODUCTION_STAGES = ['Concept', 'Planning', 'Filming / Live', 'Editing', 'Optimization', 'Scheduling', 'Published'];
export const PLATFORMS = ['YouTube', 'TikTok', 'Instagram', 'Facebook'];
export const CONTENT_FORMATS = ['Short-Form Video', 'Long-Form Video', 'Livestream', 'Carousel/Post'];
export const PRIORITIES = ['Low', 'Medium', 'High'];
export const TASK_STATUSES = ['Not Started', 'In Progress', 'Done'];

export const uid = () => `${Date.now()}-${Math.floor(Math.random() * 10000)}`;

export const INITIAL_GOALS = [
  { id: 'goal-1', name: 'Reach $5,000 / month', category: 'Revenue', target: 5000, current: 3200, unit: '$', targetDate: '2026-06-30', description: 'Grow monthly recurring income from sponsorships and ad revenue.' },
  { id: 'goal-2', name: 'Grow to 50K subscribers', category: 'Audience', target: 50000, current: 38500, unit: 'subs', targetDate: '2026-12-31', description: 'Build a loyal subscriber base across YouTube and TikTok.' },
];

export const INITIAL_STRATEGIES = [
  { id: 'strat-1', name: 'Short-Form Blitz', goalId: 'goal-1', platforms: ['YouTube', 'TikTok', 'Instagram'], formats: ['Short-Form Video', 'Livestream'], frequency: '20 videos / week', timeframe: 'Next 8 weeks', description: 'Publish 20 short-form videos per week and host two livestreams weekly to drive sponsorship inbound.', outputTarget: '20 shorts + 2 livestreams / week' },
];

export const INITIAL_PROJECTS = [
  { id: 'proj-1', name: 'Q1 Sponsor Campaign', stage: 'Editing', owner: 'You', dueDate: '2026-03-01', linkedGoalId: 'goal-1', linkedStrategyId: 'strat-1', progressPct: 55 },
  { id: 'proj-2', name: 'Weekly Livestream Series', stage: 'Scheduling', owner: 'Jordan (Editor)', dueDate: '2026-02-20', linkedGoalId: 'goal-2', linkedStrategyId: '', progressPct: 80 },
];

export const INITIAL_TASKS = [
  { id: 'task-1', title: 'Script next 3 shorts', projectId: 'proj-1', owner: 'You', dueDate: '2026-02-14', priority: 'High', status: 'In Progress' },
  { id: 'task-2', title: 'Confirm livestream guest', projectId: 'proj-2', owner: 'Jordan (Editor)', dueDate: '2026-02-16', priority: 'Medium', status: 'Not Started' },
];

export const INITIAL_MILESTONES = [
  { id: 'mile-1', name: 'Publish 20 clips this week', goalId: 'goal-1', target: 20, current: 13, unit: 'clips' },
  { id: 'mile-2', name: 'Complete 4 episodes this month', goalId: 'goal-2', target: 4, current: 2, unit: 'episodes' },
];

const EMPTY_GOAL = { name: '', category: 'Revenue', target: '', current: '', targetDate: '', description: '' };
const EMPTY_STRATEGY = { name: '', goalId: '', platforms: [], formats: [], frequency: '', timeframe: '', description: '', outputTarget: '' };
const EMPTY_PROJECT = { name: '', stage: 'Concept', owner: '', dueDate: '', linkedGoalId: '', linkedStrategyId: '', progressPct: 0 };
const EMPTY_TASK = { title: '', projectId: '', owner: '', dueDate: '', priority: 'Medium', status: 'Not Started' };
const EMPTY_MILESTONE = { name: '', goalId: '', target: '', current: '' };

function toggleInArray(arr, value) {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Goals & Strategies section — goals, strategies, and personal milestones,
 * with their existing create/edit/delete dialogs. State is passed in from
 * Project Manager so it stays consistent whether this is opened from the
 * Goals & Strategies tile or referenced from Milestones & Rewards. */
export const GoalsStrategiesSection = ({ goals, setGoals, strategies, setStrategies, milestones, setMilestones }) => {
  // ── Goal dialog ──
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [goalForm, setGoalForm] = useState(EMPTY_GOAL);
  const openCreateGoal = () => { setEditingGoalId(null); setGoalForm(EMPTY_GOAL); setGoalDialogOpen(true); };
  const openEditGoal = (goal) => { setEditingGoalId(goal.id); setGoalForm({ ...goal }); setGoalDialogOpen(true); };
  const saveGoal = () => {
    if (!goalForm.name.trim()) { toast.error('Goal name is required'); return; }
    if (editingGoalId) {
      setGoals((prev) => prev.map((g) => (g.id === editingGoalId ? { ...g, ...goalForm, target: Number(goalForm.target) || 0, current: Number(goalForm.current) || 0 } : g)));
      toast.success('Demo goal updated (not saved)');
    } else {
      setGoals((prev) => [...prev, { id: uid(), ...goalForm, target: Number(goalForm.target) || 0, current: Number(goalForm.current) || 0 }]);
      toast.success('Demo goal created (not saved)');
    }
    setGoalDialogOpen(false);
  };
  const removeGoal = (id) => {
    setGoals((prev) => prev.filter((g) => g.id !== id));
    setStrategies((prev) => prev.map((s) => (s.goalId === id ? { ...s, goalId: '' } : s)));
    toast.success('Demo goal removed');
  };

  // ── Strategy dialog ──
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [editingStrategyId, setEditingStrategyId] = useState(null);
  const [strategyForm, setStrategyForm] = useState(EMPTY_STRATEGY);
  const openCreateStrategy = () => { setEditingStrategyId(null); setStrategyForm(EMPTY_STRATEGY); setStrategyDialogOpen(true); };
  const openEditStrategy = (s) => { setEditingStrategyId(s.id); setStrategyForm({ ...s }); setStrategyDialogOpen(true); };
  const saveStrategy = () => {
    if (!strategyForm.name.trim()) { toast.error('Strategy name is required'); return; }
    if (editingStrategyId) {
      setStrategies((prev) => prev.map((s) => (s.id === editingStrategyId ? { ...s, ...strategyForm } : s)));
      toast.success('Demo strategy updated (not saved)');
    } else {
      setStrategies((prev) => [...prev, { id: uid(), ...strategyForm }]);
      toast.success('Demo strategy created (not saved)');
    }
    setStrategyDialogOpen(false);
  };
  const removeStrategy = (id) => {
    setStrategies((prev) => prev.filter((s) => s.id !== id));
    toast.success('Demo strategy removed');
  };

  // ── Milestone dialog ──
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState(EMPTY_MILESTONE);
  const openCreateMilestone = () => { setMilestoneForm(EMPTY_MILESTONE); setMilestoneDialogOpen(true); };
  const saveMilestone = () => {
    if (!milestoneForm.name.trim()) { toast.error('Milestone name is required'); return; }
    setMilestones((prev) => [...prev, { id: uid(), ...milestoneForm, target: Number(milestoneForm.target) || 0, current: Number(milestoneForm.current) || 0 }]);
    toast.success('Personal milestone created (not saved)');
    setMilestoneDialogOpen(false);
  };
  const removeMilestone = (id) => setMilestones((prev) => prev.filter((m) => m.id !== id));

  const goalName = (id) => goals.find((g) => g.id === id)?.name || '—';

  return (
    <div data-testid="goals-strategies-section">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#8A93A6]">Simulated planning — illustrative values, not guaranteed outcomes.</p>
        <Button size="sm" variant="outline" className="border-white/10" onClick={openCreateGoal} data-testid="create-goal-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />New Goal
        </Button>
      </div>
      <div className="space-y-3">
        {goals.map((goal) => {
          const pct = goal.target ? Math.min(100, Math.round((goal.current / goal.target) * 100)) : 0;
          const linkedStrategies = strategies.filter((s) => s.goalId === goal.id);
          return (
            <Card key={goal.id} className="bg-black/30 border-white/5" data-testid={`goal-card-${goal.id}`}>
              <CardContent className="p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white">{goal.name}</p>
                      <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10">{goal.category}</Badge>
                    </div>
                    <p className="text-[11px] text-[#8A93A6] mt-1">{goal.description}</p>
                    <p className="text-[11px] text-[#8A93A6] mt-1">
                      {goal.unit === '$' ? '$' : ''}{goal.current}{goal.unit !== '$' ? ` ${goal.unit}` : ''} of {goal.unit === '$' ? '$' : ''}{goal.target}{goal.unit !== '$' ? ` ${goal.unit}` : ''} · Target date {goal.targetDate || '—'}
                    </p>
                    <Progress value={pct} className="h-1.5 bg-white/[0.04] [&>div]:bg-cyan-400 mt-2 w-full max-w-xs" />
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6]" onClick={() => openEditGoal(goal)} data-testid={`edit-goal-${goal.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6] hover:text-red-400" onClick={() => removeGoal(goal.id)} data-testid={`remove-goal-${goal.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {linkedStrategies.length > 0 && (
                  <div className="mt-2.5 pl-3 border-l border-white/10 space-y-1.5">
                    {linkedStrategies.map((s) => (
                      <p key={s.id} className="text-[11px] text-[#C0C7D4]">
                        <span className="text-violet-300 font-medium">Strategy:</span> {s.outputTarget || s.frequency || s.name}
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {goals.length === 0 && <p className="text-xs text-[#8A93A6] text-center py-4">No demo goals yet.</p>}
      </div>

      <div className="flex items-center justify-between mt-5 mb-3">
        <p className="text-xs font-semibold text-white">Strategies</p>
        <Button size="sm" variant="outline" className="border-white/10" onClick={openCreateStrategy} data-testid="create-strategy-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />New Strategy
        </Button>
      </div>
      <div className="space-y-2">
        {strategies.map((s) => (
          <Card key={s.id} className="bg-black/30 border-white/5" data-testid={`strategy-card-${s.id}`}>
            <CardContent className="p-3.5 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">{s.name}</p>
                <p className="text-[11px] text-[#8A93A6] mt-0.5">Goal: {goalName(s.goalId)} · {s.timeframe || 'No timeframe set'}</p>
                <p className="text-[11px] text-[#8A93A6] mt-0.5">{s.platforms.join(', ') || 'No platforms'} · {s.formats.join(', ') || 'No formats'} · {s.frequency || 'No frequency set'}</p>
                {s.description && <p className="text-[11px] text-[#C0C7D4] mt-1">{s.description}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6]" onClick={() => openEditStrategy(s)} data-testid={`edit-strategy-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6] hover:text-red-400" onClick={() => removeStrategy(s.id)} data-testid={`remove-strategy-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {strategies.length === 0 && <p className="text-xs text-[#8A93A6] text-center py-4">No demo strategies yet.</p>}
      </div>

      {/* Personal Milestones — explicitly distinct from official Rewards */}
      <div className="flex items-center justify-between mt-5 mb-3">
        <div>
          <p className="text-xs font-semibold text-white">Personal Milestones</p>
          <p className="text-[10px] text-[#555E6E]">Your own tracking — separate from official Consistency Rewards. No badges or points awarded here.</p>
        </div>
        <Button size="sm" variant="outline" className="border-white/10 shrink-0" onClick={openCreateMilestone} data-testid="create-milestone-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />New Milestone
        </Button>
      </div>
      <div className="space-y-2">
        {milestones.map((m) => {
          const pct = m.target ? Math.min(100, Math.round((m.current / m.target) * 100)) : 0;
          return (
            <Card key={m.id} className="bg-black/30 border-white/5" data-testid={`milestone-card-${m.id}`}>
              <CardContent className="p-3.5 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white">{m.name}</p>
                  <p className="text-[11px] text-[#8A93A6]">Goal: {goalName(m.goalId)} · {m.current}/{m.target} {m.unit}</p>
                  <Progress value={pct} className="h-1.5 bg-white/[0.04] [&>div]:bg-violet-400 mt-1.5 w-full max-w-xs" />
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6] hover:text-red-400 shrink-0" onClick={() => removeMilestone(m.id)} data-testid={`remove-milestone-${m.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
        {milestones.length === 0 && <p className="text-xs text-[#8A93A6] text-center py-4">No personal milestones yet.</p>}
      </div>

      {/* ── Create / Edit Goal Dialog ── */}
      <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white" data-testid="goal-dialog">
          <DialogHeader><DialogTitle>{editingGoalId ? 'Edit Goal' : 'Create Goal'} (Demo)</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-[11px] text-[#8A93A6]">Goal name</Label><Input value={goalForm.name} onChange={(e) => setGoalForm((f) => ({ ...f, name: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="goal-name-input" /></div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Category</Label>
              <Select value={goalForm.category} onValueChange={(v) => setGoalForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="goal-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{GOAL_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px] text-[#8A93A6]">Current value</Label><Input type="number" value={goalForm.current} onChange={(e) => setGoalForm((f) => ({ ...f, current: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="goal-current-input" /></div>
              <div><Label className="text-[11px] text-[#8A93A6]">Target value</Label><Input type="number" value={goalForm.target} onChange={(e) => setGoalForm((f) => ({ ...f, target: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="goal-target-input" /></div>
            </div>
            <div><Label className="text-[11px] text-[#8A93A6]">Target date</Label><Input type="date" value={goalForm.targetDate} onChange={(e) => setGoalForm((f) => ({ ...f, targetDate: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="goal-date-input" /></div>
            <div><Label className="text-[11px] text-[#8A93A6]">Description</Label><Textarea value={goalForm.description} onChange={(e) => setGoalForm((f) => ({ ...f, description: e.target.value }))} className="text-xs bg-black/40 border-white/10 mt-1 min-h-[50px]" data-testid="goal-description-input" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={saveGoal} data-testid="save-goal-btn">{editingGoalId ? 'Save Changes' : 'Create Goal'} (Demo Only)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create / Edit Strategy Dialog ── */}
      <Dialog open={strategyDialogOpen} onOpenChange={setStrategyDialogOpen}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white max-h-[85vh] overflow-y-auto" data-testid="strategy-dialog">
          <DialogHeader><DialogTitle>{editingStrategyId ? 'Edit Strategy' : 'Create Strategy'} (Demo)</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-[11px] text-[#8A93A6]">Strategy name</Label><Input value={strategyForm.name} onChange={(e) => setStrategyForm((f) => ({ ...f, name: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="strategy-name-input" /></div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Associated goal</Label>
              <Select value={strategyForm.goalId} onValueChange={(v) => setStrategyForm((f) => ({ ...f, goalId: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="strategy-goal-select"><SelectValue placeholder="Select a goal" /></SelectTrigger>
                <SelectContent>{goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-[#8A93A6] mb-1 block">Target platforms</Label>
              <div className="flex flex-wrap gap-3">
                {PLATFORMS.map((p) => (
                  <label key={p} className="flex items-center gap-1.5 text-xs text-[#8A93A6]">
                    <Checkbox checked={strategyForm.platforms.includes(p)} onCheckedChange={() => setStrategyForm((f) => ({ ...f, platforms: toggleInArray(f.platforms, p) }))} data-testid={`strategy-platform-${p}`} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-[#8A93A6] mb-1 block">Content types / formats</Label>
              <div className="flex flex-wrap gap-3">
                {CONTENT_FORMATS.map((f) => (
                  <label key={f} className="flex items-center gap-1.5 text-xs text-[#8A93A6]">
                    <Checkbox checked={strategyForm.formats.includes(f)} onCheckedChange={() => setStrategyForm((prev) => ({ ...prev, formats: toggleInArray(prev.formats, f) }))} data-testid={`strategy-format-${f}`} />
                    {f}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px] text-[#8A93A6]">Publishing frequency</Label><Input value={strategyForm.frequency} onChange={(e) => setStrategyForm((f) => ({ ...f, frequency: e.target.value }))} placeholder="e.g. 20 videos / week" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="strategy-frequency-input" /></div>
              <div><Label className="text-[11px] text-[#8A93A6]">Timeframe</Label><Input value={strategyForm.timeframe} onChange={(e) => setStrategyForm((f) => ({ ...f, timeframe: e.target.value }))} placeholder="e.g. Next 8 weeks" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="strategy-timeframe-input" /></div>
            </div>
            <div><Label className="text-[11px] text-[#8A93A6]">Planned output target</Label><Input value={strategyForm.outputTarget} onChange={(e) => setStrategyForm((f) => ({ ...f, outputTarget: e.target.value }))} placeholder="e.g. 20 shorts + 2 livestreams / week" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="strategy-output-input" /></div>
            <div><Label className="text-[11px] text-[#8A93A6]">Description</Label><Textarea value={strategyForm.description} onChange={(e) => setStrategyForm((f) => ({ ...f, description: e.target.value }))} className="text-xs bg-black/40 border-white/10 mt-1 min-h-[50px]" data-testid="strategy-description-input" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={saveStrategy} data-testid="save-strategy-btn">{editingStrategyId ? 'Save Changes' : 'Create Strategy'} (Demo Only)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Personal Milestone Dialog ── */}
      <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white" data-testid="milestone-dialog">
          <DialogHeader><DialogTitle>New Personal Milestone (Demo)</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-[11px] text-[#8A93A6]">Milestone name</Label><Input value={milestoneForm.name} onChange={(e) => setMilestoneForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Publish 20 clips this week" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="milestone-name-input" /></div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Associated goal</Label>
              <Select value={milestoneForm.goalId} onValueChange={(v) => setMilestoneForm((f) => ({ ...f, goalId: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="milestone-goal-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px] text-[#8A93A6]">Current</Label><Input type="number" value={milestoneForm.current} onChange={(e) => setMilestoneForm((f) => ({ ...f, current: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="milestone-current-input" /></div>
              <div><Label className="text-[11px] text-[#8A93A6]">Target</Label><Input type="number" value={milestoneForm.target} onChange={(e) => setMilestoneForm((f) => ({ ...f, target: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="milestone-target-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={saveMilestone} data-testid="save-milestone-btn">Create (Demo Only)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Projects & Tasks section — project-planning entries (production stages,
 * progress) and manually-planned tasks, with their existing create/edit/
 * delete dialogs and the link to the real Project Overview. */
export const ProjectsTasksSection = ({ goals, strategies, projects, setProjects, tasks, setTasks }) => {
  // ── Project dialog ──
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);
  const openCreateProject = () => { setProjectForm(EMPTY_PROJECT); setProjectDialogOpen(true); };
  const saveProject = () => {
    if (!projectForm.name.trim()) { toast.error('Project name is required'); return; }
    setProjects((prev) => [...prev, { id: uid(), ...projectForm, progressPct: Number(projectForm.progressPct) || 0 }]);
    toast.success('Demo project-planning entry created (not a real Content Project)');
    setProjectDialogOpen(false);
  };
  const updateProjectField = (id, field, value) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };
  const removeProject = (id) => setProjects((prev) => prev.filter((p) => p.id !== id));

  // ── Task dialog ──
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK);
  const openCreateTask = () => { setTaskForm(EMPTY_TASK); setTaskDialogOpen(true); };
  const saveTask = () => {
    if (!taskForm.title.trim()) { toast.error('Task title is required'); return; }
    setTasks((prev) => [...prev, { id: uid(), ...taskForm }]);
    toast.success('Demo task created (not saved)');
    setTaskDialogOpen(false);
  };
  const toggleTaskDone = (id) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status: t.status === 'Done' ? 'Not Started' : 'Done' } : t)));
  };
  const removeTask = (id) => setTasks((prev) => prev.filter((t) => t.id !== id));

  const goalName = (id) => goals.find((g) => g.id === id)?.name || '—';
  const strategyName = (id) => strategies.find((s) => s.id === id)?.name || '—';

  return (
    <div data-testid="projects-tasks-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs text-[#8A93A6] flex items-center gap-1.5">
          <Info className="h-3 w-3" />
          Production planning only — does not create a real Content Project.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-white/10" onClick={openCreateProject} data-testid="create-project-planning-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" />New Project-Planning Entry
          </Button>
          <a href="/app/create/workspace" data-testid="open-project-overview-link">
            <Button size="sm" variant="outline" className="border-white/10">
              Open Project Overview
              <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </a>
        </div>
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <Card key={p.id} className="bg-black/30 border-white/5" data-testid={`project-plan-card-${p.id}`}>
            <CardContent className="p-3.5">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{p.name}</p>
                  <p className="text-[11px] text-[#8A93A6] mt-0.5">
                    Owner: {p.owner || 'Unassigned'} · Due {p.dueDate || '—'} · Goal: {goalName(p.linkedGoalId)}{p.linkedStrategyId ? ` · Strategy: ${strategyName(p.linkedStrategyId)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={p.stage} onValueChange={(v) => updateProjectField(p.id, 'stage', v)}>
                    <SelectTrigger className="h-7 w-[150px] text-xs bg-black/40 border-white/10" data-testid={`project-stage-select-${p.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCTION_STAGES.map((stg) => <SelectItem key={stg} value={stg}>{stg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6] hover:text-red-400" onClick={() => removeProject(p.id)} data-testid={`remove-project-plan-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <Progress value={p.progressPct} className="h-1.5 bg-white/[0.04] [&>div]:bg-green-400 flex-1 max-w-xs" />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={p.progressPct}
                  onChange={(e) => updateProjectField(p.id, 'progressPct', Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="h-7 w-16 text-xs bg-black/40 border-white/10"
                  data-testid={`project-progress-input-${p.id}`}
                />
                <span className="text-[11px] text-[#8A93A6]">%</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {projects.length === 0 && <p className="text-xs text-[#8A93A6] text-center py-4">No project-planning entries yet.</p>}
      </div>

      {/* Tasks & Assignments */}
      <div className="flex items-center justify-between mt-5 mb-3">
        <div>
          <p className="text-xs font-semibold text-white">Tasks & Assignments</p>
          <p className="text-[10px] text-[#555E6E]">Manually planned tasks — separate from Creator Task Engine recommendations in Growth Intelligence.</p>
        </div>
        <Button size="sm" variant="outline" className="border-white/10 shrink-0" onClick={openCreateTask} data-testid="create-task-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />New Task
        </Button>
      </div>
      <div className="space-y-1.5">
        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2" data-testid={`task-row-${t.id}`}>
            <button type="button" onClick={() => toggleTaskDone(t.id)} data-testid={`toggle-task-${t.id}`}>
              {t.status === 'Done' ? <CheckCircle2 className="h-4 w-4 text-green-400" /> : <Circle className="h-4 w-4 text-[#555E6E]" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-[13px] ${t.status === 'Done' ? 'text-[#6B7280] line-through' : 'text-white'}`}>{t.title}</p>
              <p className="text-[10px] text-[#8A93A6]">{projects.find((p) => p.id === t.projectId)?.name || 'No project'} · {t.owner || 'Unassigned'} · Due {t.dueDate || '—'}</p>
            </div>
            <Badge variant="outline" className={`text-[10px] shrink-0 ${t.priority === 'High' ? 'border-red-500/30 text-red-300 bg-red-500/10' : t.priority === 'Medium' ? 'border-amber-500/30 text-amber-300 bg-amber-500/10' : 'border-white/10 text-[#8A93A6]'}`}>
              {t.priority}
            </Badge>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8A93A6] hover:text-red-400 shrink-0" onClick={() => removeTask(t.id)} data-testid={`remove-task-${t.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        {tasks.length === 0 && <p className="text-xs text-[#8A93A6] text-center py-4">No manual tasks yet.</p>}
      </div>

      {/* ── Create Project-Planning Entry Dialog ── */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white" data-testid="project-plan-dialog">
          <DialogHeader><DialogTitle>New Project-Planning Entry (Demo)</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-[11px] text-[#8A93A6]">Project name</Label><Input value={projectForm.name} onChange={(e) => setProjectForm((f) => ({ ...f, name: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="project-plan-name-input" /></div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Production stage</Label>
              <Select value={projectForm.stage} onValueChange={(v) => setProjectForm((f) => ({ ...f, stage: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="project-plan-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRODUCTION_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px] text-[#8A93A6]">Owner (mock)</Label><Input value={projectForm.owner} onChange={(e) => setProjectForm((f) => ({ ...f, owner: e.target.value }))} placeholder="e.g. You, Jordan (Editor)" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="project-plan-owner-input" /></div>
              <div><Label className="text-[11px] text-[#8A93A6]">Due date</Label><Input type="date" value={projectForm.dueDate} onChange={(e) => setProjectForm((f) => ({ ...f, dueDate: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="project-plan-duedate-input" /></div>
            </div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Associated goal</Label>
              <Select value={projectForm.linkedGoalId} onValueChange={(v) => setProjectForm((f) => ({ ...f, linkedGoalId: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="project-plan-goal-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Associated strategy</Label>
              <Select value={projectForm.linkedStrategyId} onValueChange={(v) => setProjectForm((f) => ({ ...f, linkedStrategyId: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="project-plan-strategy-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{strategies.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={saveProject} data-testid="save-project-plan-btn">Create (Demo Only)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Task Dialog ── */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white" data-testid="task-dialog">
          <DialogHeader><DialogTitle>New Task (Demo)</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-[11px] text-[#8A93A6]">Task title</Label><Input value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="task-title-input" /></div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Associated project</Label>
              <Select value={taskForm.projectId} onValueChange={(v) => setTaskForm((f) => ({ ...f, projectId: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="task-project-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px] text-[#8A93A6]">Assigned owner (mock)</Label><Input value={taskForm.owner} onChange={(e) => setTaskForm((f) => ({ ...f, owner: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="task-owner-input" /></div>
              <div><Label className="text-[11px] text-[#8A93A6]">Due date</Label><Input type="date" value={taskForm.dueDate} onChange={(e) => setTaskForm((f) => ({ ...f, dueDate: e.target.value }))} className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="task-duedate-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-[#8A93A6]">Priority</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6]">Status</Label>
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={saveTask} data-testid="save-task-btn">Create (Demo Only)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
